'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { ProcedureForm } from '../-components/ProcedureForm';
import { ProcedurePageShell } from '../-components/ProcedurePageShell';
import type { ProcedureFormSchema } from '../-schema';

// 入社手続き（従業員側）。労務コアの入社 Data ビューの項目整理を踏襲しつつ、
// 本人が入力する項目だけに絞る（雇用契約抽出確認・招待送付・社内承認は労務側のため除外）。
const ONBOARDING_SCHEMA: ProcedureFormSchema = {
  title: '入社手続き',
  personName: '青柳 誠',
  personBadges: ['正社員', '入社予定'],
  personMeta: [{
    label: '入社日',
    value: '2025年5月1日',
    emphasized: true
  }, {
    label: '配属',
    value: 'プラットフォーム本部開発部'
  }, {
    label: '雇用形態',
    value: '正社員（フルタイム）'
  }],
  submitLabel: '入社手続きを提出する',
  submittedMessage: '入社手続きを提出しました。',
  flow: 'assigned',
  requestId: 'ipr-onboarding',
  requestName: '入社手続き',
  requestTotalSteps: 9,
  route: '/individual/portal/procedures/onboarding',
  sections: [{
    id: 'basic-info',
    title: '① 基本情報',
    fields: [{
      kind: 'text',
      id: 'name',
      label: '氏名',
      required: true,
      defaultValue: '青柳 誠'
    }, {
      kind: 'text',
      id: 'nameKana',
      label: 'フリガナ',
      defaultValue: 'アオタナ マコト'
    }, {
      kind: 'date',
      id: 'birthday',
      label: '生年月日',
      defaultValue: '1991-06-08'
    }, {
      kind: 'radio',
      id: 'gender',
      label: '性別',
      defaultValue: 'male',
      options: [{
        value: 'male',
        label: '男性'
      }, {
        value: 'female',
        label: '女性'
      }, {
        value: 'na',
        label: '回答しない'
      }]
    }, {
      kind: 'radio',
      id: 'maritalStatus',
      label: '婚姻状況',
      defaultValue: 'married',
      options: [{
        value: 'married',
        label: '既婚'
      }, {
        value: 'single',
        label: '未婚'
      }]
    }, {
      kind: 'select',
      id: 'bloodType',
      label: '血液型',
      defaultValue: 'ab',
      placeholder: '選択してください',
      options: [{
        value: 'a',
        label: 'A型'
      }, {
        value: 'b',
        label: 'B型'
      }, {
        value: 'o',
        label: 'O型'
      }, {
        value: 'ab',
        label: 'AB型'
      }]
    }]
  }, {
    id: 'address',
    title: '② 現住所・住民票',
    fields: [{
      kind: 'text',
      id: 'postalCode',
      label: '郵便番号',
      required: true,
      defaultValue: '104-0051'
    }, {
      kind: 'text',
      id: 'prefecture',
      label: '都道府県',
      required: true,
      defaultValue: '東京都'
    }, {
      kind: 'text',
      id: 'city',
      label: '市区町村',
      required: true,
      defaultValue: '中央区'
    }, {
      kind: 'text',
      id: 'address',
      label: '番地',
      required: true,
      defaultValue: '佃 3-6-5'
    }, {
      kind: 'text',
      id: 'building',
      label: '建物名・部屋番号',
      defaultValue: 'パークハビオ月島フロント 303'
    }, {
      kind: 'radio',
      id: 'residencyMatch',
      label: '住民票の住所は現住所と同じですか',
      required: true,
      defaultValue: 'same',
      options: [{
        value: 'same',
        label: '同じ'
      }, {
        value: 'different',
        label: '異なる'
      }]
    }]
  }, {
    id: 'emergency-contact',
    title: '③ 緊急連絡先',
    fields: [{
      kind: 'text',
      id: 'ecName',
      label: '氏名',
      required: true,
      defaultValue: '青柳 淳一'
    }, {
      kind: 'text',
      id: 'ecRelation',
      label: '続柄',
      defaultValue: '父'
    }, {
      kind: 'text',
      id: 'ecPhone',
      label: '電話番号',
      required: true,
      defaultValue: '090-8888-0000'
    }]
  }, {
    id: 'mynumber',
    title: '④ マイナンバー・扶養家族',
    description: 'マイナンバーの利用目的（登録目的）は会社が設定しています。以下の目的での利用をご確認のうえ、番号と本人確認書類を提出してください。高機密情報として暗号化して保管され、下記の目的以外には利用しません。',
    fields: [{
      kind: 'readonlyList',
      id: 'mynumberPurpose',
      label: '登録目的（会社設定・確認のみ）',
      items: ['源泉徴収票・給与支払報告書の作成に係る事務', '健康保険・厚生年金保険関係届出事務', '雇用保険関係届出事務']
    }, {
      kind: 'text',
      id: 'myNumber',
      label: 'マイナンバー（個人番号）',
      required: true,
      placeholder: '12桁の番号'
    }, {
      kind: 'file',
      id: 'identityDoc',
      label: '本人確認書類の画像',
      required: true,
      accept: 'image/*,application/pdf',
      hint: 'マイナンバーカード（表面）／通知カード＋本人確認書類。画像 or PDF'
    }, {
      kind: 'radio',
      id: 'dependents',
      label: '扶養家族',
      required: true,
      defaultValue: 'yes',
      options: [{
        value: 'yes',
        label: 'あり'
      }, {
        value: 'no',
        label: 'なし'
      }]
    }, {
      kind: 'text',
      id: 'dependentName',
      label: '扶養家族の氏名（配偶者）',
      defaultValue: '青柳 ゆみ'
    }]
  }, {
    id: 'social-insurance',
    title: '⑤ 社会保険・年金',
    description: '前職がある場合は、雇用保険被保険者番号・基礎年金番号をご記入ください（社会保険・雇用保険の資格取得手続きに使用）。',
    fields: [{
      kind: 'text',
      id: 'pensionNumber',
      label: '基礎年金番号',
      placeholder: '例: 1234-567890'
    }, {
      kind: 'text',
      id: 'empInsuranceNumber',
      label: '雇用保険被保険者番号（前職）',
      placeholder: '11桁'
    }]
  }, {
    id: 'payroll-account',
    title: '⑥ 給与振込口座',
    fields: [{
      kind: 'text',
      id: 'bankName',
      label: '銀行名',
      required: true,
      defaultValue: 'みずほ銀行'
    }, {
      kind: 'text',
      id: 'branchName',
      label: '支店名',
      required: true,
      defaultValue: '月島支店'
    }, {
      kind: 'radio',
      id: 'accountType',
      label: '口座種別',
      defaultValue: 'ordinary',
      options: [{
        value: 'ordinary',
        label: '普通'
      }, {
        value: 'checking',
        label: '当座'
      }]
    }, {
      kind: 'text',
      id: 'accountNumber',
      label: '口座番号',
      required: true,
      defaultValue: '1234567'
    }, {
      kind: 'text',
      id: 'accountHolder',
      label: '口座名義（カナ）',
      defaultValue: 'アオタナ マコト'
    }]
  }, {
    id: 'commute',
    title: '⑦ 通勤・交通費',
    fields: [{
      kind: 'radio',
      id: 'commuteMethod',
      label: '通勤手段',
      defaultValue: 'train',
      options: [{
        value: 'train',
        label: '電車・バス'
      }, {
        value: 'car',
        label: '自家用車'
      }, {
        value: 'walk',
        label: '徒歩・自転車'
      }]
    }, {
      kind: 'textarea',
      id: 'commuteRoute',
      label: '通勤経路',
      placeholder: '例: 渋谷駅 → 東京駅（JR山手線）',
      defaultValue: '月島駅 → 大手町駅（東京メトロ有楽町線）'
    }, {
      kind: 'text',
      id: 'commuteCost',
      label: '1か月の交通費（定期代）',
      placeholder: '例: ¥ 15,000',
      defaultValue: '¥ 6,600'
    }]
  }, {
    id: 'education-cert',
    title: '⑧ 学歴・資格',
    fields: [{
      kind: 'text',
      id: 'lastEducation',
      label: '最終学歴',
      placeholder: '例: ○○大学 △△学部 卒業',
      defaultValue: '東京理工大学 情報理工学部 卒業'
    }, {
      kind: 'textarea',
      id: 'certifications',
      label: '保有資格',
      placeholder: '例: 基本情報技術者、TOEIC 850',
      defaultValue: '基本情報技術者、応用情報技術者'
    }]
  }, {
    id: 'health-checkup',
    title: '⑨ 健康診断',
    description: '直近の健康診断の受診状況をご記入ください。',
    fields: [{
      kind: 'radio',
      id: 'healthCheckup',
      label: '直近1年以内の健康診断',
      required: true,
      defaultValue: 'done',
      options: [{
        value: 'done',
        label: '受診済み'
      }, {
        value: 'notyet',
        label: '未受診'
      }]
    }, {
      kind: 'file',
      id: 'healthResult',
      label: '健康診断結果（任意）',
      accept: 'image/*,application/pdf',
      hint: '結果票の画像またはPDF（任意）'
    }]
  }]
};
export const Route = createFileRoute('/_individual/individual/portal/procedures/onboarding/')({
  component: OnboardingPage
});
function OnboardingPage() {
  return <ProcedurePageShell>
      <ProcedureForm schema={ONBOARDING_SCHEMA} />
    </ProcedurePageShell>;
}